import React from 'react';

import { Chip, ChipProps, Tooltip } from '@mui/material';

interface Props extends Omit<ChipProps, 'onClick'> {
  /**
   * Admin path to open in a new tab when the chip body is clicked. If undefined (e.g. vendor id
   * not yet resolved for a rate plan), the chip stays inert — click does nothing.
   */
  href?: string;
  /** Tooltip text. When `href` is undefined, this is also what's shown on hover. */
  tooltip?: string;
}

/**
 * Renders an MUI Chip whose body opens `href` in a new tab on plain click. Falls back to inert
 * (with a tooltip explaining why) when `href` isn't available yet. The built-in delete (×)
 * affordance from `getTagProps` continues to work — MUI fires the delete handler off the icon's
 * own click, not the chip body click.
 */
function LinkableChip({ href, tooltip, ...chipProps }: Props) {
  const handleClick = () => {
    if (!href) return;
    window.open(href, '_blank', 'noopener,noreferrer');
  };

  const resolvedTooltip = tooltip ?? (href ? `Open ${href}` : '');

  const chip = <Chip {...chipProps} onClick={href ? handleClick : undefined} />;

  if (!resolvedTooltip) return chip;
  return <Tooltip title={resolvedTooltip}>{chip}</Tooltip>;
}

export default LinkableChip;
